import type { FC } from "react";
import React, { useMemo } from "react";

import { getChildrenRecord } from "../../../../../utils/react";
import { UniBoxAdditional } from "../../../../UniBox";
import {
    BADGE_SIZE_BY_AVA_PICTURE_SIZE_MAP,
    GIFT_SIZE_BY_AVA_PICTURE_SIZE_MAP,
} from "../../../constants/sizes";
import { createDisplayNameForAvaPictureElement } from "../../../utils/getDisplayName";
import { createOffsetClassMap } from "../../../utils/getOffsetClassMap";
import { createTestIdForAvaPictureElement } from "../../../utils/getTestId";

import type { AvaPictureShapes, AvaPictureSizes } from "../../../types";

import classes from "./AvaPictureSlotBottomRight.styl";

type AvaPictureSlotBottomRightPrivateProps = {
    size: AvaPictureSizes;
    shape: AvaPictureShapes;
    children: React.ReactElement;
};

type AvaPictureSlotBottomRightProps = Omit<
    AvaPictureSlotBottomRightPrivateProps,
    "size" | "shape"
>;

const AVA_PICTURE_SLOT_BOTTOM_RIGHT_DISPLAY_NAME =
    createDisplayNameForAvaPictureElement("SlotBottomRight");

const AVA_PICTURE_SLOT_BOTTOM_RIGHT_TEST_ID =
    createTestIdForAvaPictureElement("SlotBottomRight");

const BADGE_OFFSET_CLASS_BY_SIZE_MAPS = {
    circle: createOffsetClassMap("badge-offsets-circle", classes),
    squircle: createOffsetClassMap("badge-offsets-squircle", classes),
};

export const AvaPictureSlotBottomRight: FC<AvaPictureSlotBottomRightProps> = (
    props,
) => {
    const { size, shape, children } =
        props as AvaPictureSlotBottomRightPrivateProps;

    const { Badge, "AvaPicture.Gift": Gift } = useMemo(
        () =>
            getChildrenRecord(
                children,
                AVA_PICTURE_SLOT_BOTTOM_RIGHT_DISPLAY_NAME,
                {
                    Badge: {
                        props: {
                            size: BADGE_SIZE_BY_AVA_PICTURE_SIZE_MAP[size],
                        },
                        optionalRequired: true,
                    },
                    "AvaPicture.Gift": {
                        props: {
                            width: GIFT_SIZE_BY_AVA_PICTURE_SIZE_MAP[size],
                            height: GIFT_SIZE_BY_AVA_PICTURE_SIZE_MAP[size],
                        },
                        optionalRequired: true,
                    },
                    Unknown: {
                        skipElement: true,
                    },
                },
            ),
        [children, size],
    );

    const offsetClass = (() => {
        if (Badge) {
            return BADGE_OFFSET_CLASS_BY_SIZE_MAPS[shape][size];
        }
        if (Gift) {
            return classes["gift-offset"];
        }
        return "";
    })();

    const childToRender = Badge || Gift;

    const shouldShowChild = (() => {
        if (Badge) {
            return ["16", "20", "24"].indexOf(size) === -1;
        }
        if (Gift) {
            return ["16", "20", "24", "36", "44"].indexOf(size) === -1;
        }
        return false;
    })();

    return (
        <UniBoxAdditional.SlotBottomRight
            data-tsid={AVA_PICTURE_SLOT_BOTTOM_RIGHT_TEST_ID}
            customVarsStyles={offsetClass}
        >
            {shouldShowChild && childToRender}
        </UniBoxAdditional.SlotBottomRight>
    );
};

AvaPictureSlotBottomRight.displayName =
    AVA_PICTURE_SLOT_BOTTOM_RIGHT_DISPLAY_NAME;

export default AvaPictureSlotBottomRight;
